<script setup lang="ts">
import { computed } from 'vue'
import { BookOpen } from '@lucide/vue'
import { Progress } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from '../../stores/library'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import type { LibraryEntry } from '../../lib/library-entries'
import { comicStatus } from '../../lib/comic-status'
import { useComicCover } from '../../lib/comic-cover'
import { pickReadingProgress, readingProgressRatio } from '../../lib/reading-progress-summary'
import ComicCardMenu from './comic-card-menu.vue'
import ComicSelectBox from './comic-select-box.vue'
import ComicStatusDot from './comic-status-dot.vue'

const props = defineProps<{ entry: LibraryEntry }>()
const emit = defineEmits<{ info: [entry: LibraryEntry]; edit: [entry: LibraryEntry]; read: [entry: LibraryEntry]; backup: [entry: LibraryEntry]; relink: [entry: LibraryEntry]; remove: [entry: LibraryEntry] }>()
const store = useLibraryStore()
const cloud = useCloudLibraryStore()
const entries = useLibraryEntriesStore()
const comic = computed(() => props.entry.comic)
// 封面從哪裡來跟著這一列有沒有本機那一份走，不跟著「目前在哪一頁」。
const coverComic = computed(() => props.entry.local ?? props.entry.comic)
const coverScope = computed(() => props.entry.local ? 'local' as const : 'cloud' as const)
const { element, cover, failed, fallbackText } = useComicCover(() => coverComic.value, () => coverScope.value)
const available = computed(() => props.entry.readable)
const selecting = computed(() => entries.selectionMode)
const selected = computed(() => entries.selection.has(props.entry.id))
// 備份只有持有本機檔案的裝置做得到，而且只對還沒備份完成的作品。
const canBackup = computed(() => !!props.entry.local && !props.entry.backedUp)
// 本機那一份找不到檔案時才有「更新位置」：資料夾被搬走或改名，接到新位置就回來了。
const canRelink = computed(() => !!props.entry.local && store.missing.has(props.entry.local.id))
// 要點哪一顆燈由 comic-status 決定，卡片與列表列因此不會各自挑一套規則。
const status = computed(() => comicStatus({
  hasLocalCopy: !!props.entry.local,
  syncState: props.entry.comic.syncState,
  missing: !!props.entry.local && store.missing.has(props.entry.local.id),
  indexFailed: !!props.entry.local && !!store.indexErrors[props.entry.local.id],
  indexing: !!props.entry.local && store.indexing.has(props.entry.local.id),
}))
// 本機那一份記在本機識別碼上，雲端那一份記在目錄項上；取較新者，iPad 上沒有本機副本也看得到。
const progressPercent = computed(() => {
  const local = props.entry.local ? store.progress.get(props.entry.local.id) : undefined
  const remote = props.entry.comic.cloudComicId ? cloud.progress.get(props.entry.comic.cloudComicId) : undefined
  const picked = pickReadingProgress(local, remote)
  return picked ? Math.round(readingProgressRatio(picked.page, props.entry.comic.pageCount) * 100) : null
})
function selectAction(key: 'info' | 'organize' | 'backup' | 'relink' | 'remove') {
  if (key === 'info') { emit('info', props.entry); return }
  if (key === 'backup') { emit('backup', props.entry); return }
  if (key === 'relink') { emit('relink', props.entry); return }
  if (key === 'remove') { emit('remove', props.entry); return }
  emit('edit', props.entry)
}
</script>

<template>
  <article ref="element" class="comic-card">
    <div class="cover-stage" data-row-action-reveal-scope>
      <!-- 多選模式下同一次點擊只代表一件事：切換選取，不開啟閱讀。 -->
      <button v-if="selecting" type="button" class="comic-read-link" :aria-label="`選取 ${comic.title}`" @click="event => entries.toggleSelected(entry.id, event.shiftKey)" />
      <button v-else-if="available" type="button" class="comic-read-link" :aria-label="`閱讀 ${comic.title}`" @click="emit('read', entry)" />
      <ComicSelectBox v-if="selecting" class="comic-card-select" :selected="selected" :title="comic.title" @toggle="extend => entries.toggleSelected(entry.id, extend)" />
      <img v-if="cover && !failed" :src="cover" :alt="`${comic.title}，第 ${comic.coverPage + 1} 頁封面`" class="comic-cover" @error="failed = true" />
      <div v-else class="cover-fallback"><BookOpen :size="28" :stroke-width="1" aria-hidden="true" /><span>{{ fallbackText }}</span></div>
      <!--
        狀態一律是一顆燈，不是一段文字標籤：封面上能讓出來的位置只有那麼大，而文字標籤
        貼在深淺不一的封面上本來就不好讀。說明改在游標停留時出現。
      -->
      <ComicStatusDot v-if="status" class="cover-status" :status="status" />
      <ComicCardMenu
        class="comic-card-menu"
        :title="comic.title"
        :data-comic-id="entry.id"
        :can-backup="canBackup"
        :can-relink="canRelink"
        @select="selectAction"
      />
    </div>
    <div class="comic-caption">
      <h3 :title="comic.title">{{ comic.title }}</h3>
      <Progress
        v-if="progressPercent !== null"
        class="comic-progress"
        size="sm"
        color="primary"
        :value="progressPercent"
        :aria-label="`${comic.title} 的閱讀進度 ${progressPercent}%`"
      />
    </div>
  </article>
</template>
